import {ChangeDetectionStrategy, Component, effect, ElementRef, input, signal, viewChild} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {durationDisplayToSeconds, DurationType, secondsToDurationDisplay} from 'src/app/shared/utils/duration.util';

/**
 * Duration input component with 3 fields: hours, minutes, seconds.
 * Form control value is stored as total seconds (string), not a number.
 * Display uses text inputs (not number) to support leading zeros (e.g., "04:05").
 */
@Component({
  selector: 'oph-duration-input',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './oph-duration-input.component.html',
  styleUrl: './oph-duration-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OphDurationInputComponent {
  /** Form control expects string (total seconds) or null */
  control = input.required<FormControl<string | null>>();
  /** Duration type determines which input fields are displayed */
  durationType = input<'hrs' | 'hrs/min' | 'min' | 'min/sec'>('hrs/min');
  fromTrigger = input<boolean>(false);
  backgroundColor = input<string>('var(--lightest)');

  private hoursInput = viewChild<ElementRef<HTMLInputElement>>('hoursInput');
  private minutesInput = viewChild<ElementRef<HTMLInputElement>>('minutesInput');
  private secondsInput = viewChild<ElementRef<HTMLInputElement>>('secondsInput');

  /** Display values - kept separate from form value for formatting (leading zeros) */
  hours = signal<string>('');
  minutes = signal<string>('');
  seconds = signal<string>('');

  constructor() {
    // Sync form control value -> display signals when control value changes externally
    effect(
      () => {
        const rawValue = this.control().value;
        if (rawValue === null || rawValue === undefined || rawValue === '') {
          this.hours.set('');
          this.minutes.set('');
          this.seconds.set('');
          return;
        }

        const totalSeconds = typeof rawValue === 'string' ? parseInt(rawValue, 10) : Number(rawValue);
        if (isNaN(totalSeconds)) {
          this.hours.set('');
          this.minutes.set('');
          this.seconds.set('');
          return;
        }

        const type = this.durationType() as DurationType;
        const d = secondsToDurationDisplay(totalSeconds, type);
        const {primary, secondary} = d;

        switch (type) {
          case 'hrs/min':
            this.hours.set(primary > 0 ? String(primary) : '');
            this.minutes.set(primary > 0 || secondary > 0 ? String(secondary).padStart(2, '0') : '');
            this.seconds.set('');
            break;
          case 'min/sec':
            this.hours.set('');
            this.minutes.set(primary > 0 ? String(primary) : '');
            this.seconds.set(primary > 0 || secondary > 0 ? String(secondary).padStart(2, '0') : '');
            break;
          case 'hrs':
            this.hours.set(primary > 0 ? String(primary) : '');
            this.minutes.set('');
            this.seconds.set('');
            break;
          case 'min':
            this.hours.set('');
            this.minutes.set(primary > 0 ? String(primary) : '');
            this.seconds.set('');
            break;
          case 'sec':
            this.hours.set('');
            this.minutes.set('');
            this.seconds.set(primary > 0 ? String(primary).padStart(2, '0') : '');
            break;
          default:
            this.hours.set('');
            this.minutes.set('');
            this.seconds.set('');
        }
      },
      {allowSignalWrites: true}
    );
  }

  onInput(event: Event, component: 'hours' | 'minutes' | 'seconds') {
    const target = event.target as HTMLInputElement;
    let value = target.value.replace(/\D/g, ''); // Strip non-numeric

    const maxLength = component === 'hours' ? 3 : 2;
    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
    }

    target.value = value;

    if (component === 'hours') this.hours.set(value);
    else if (component === 'minutes') this.minutes.set(value);
    else this.seconds.set(value);

    this.updateFormValue();
    this.handleAutoFocus(component, value);
  }

  /** Pad single-digit minutes/seconds with leading zero on blur (e.g., "4" -> "04") */
  onBlur(component: 'hours' | 'minutes' | 'seconds') {
    if (component === 'minutes') {
      const val = this.minutes();
      if (val && val.length === 1) {
        this.minutes.set(val.padStart(2, '0'));
      }
    } else if (component === 'seconds') {
      const val = this.seconds();
      if (val && val.length === 1) {
        this.seconds.set(val.padStart(2, '0'));
      }
    }
  }

  /** Handle backspace navigation (move to previous field) and arrow key navigation */
  onKeydown(event: KeyboardEvent, component: 'hours' | 'minutes' | 'seconds') {
    const target = event.target as HTMLInputElement;
    const type = this.durationType();

    if (event.key === 'Backspace' && target.value === '') {
      if (component === 'seconds' && type === 'min/sec') {
        this.minutesInput()?.nativeElement.focus();
        event.preventDefault();
      } else if (component === 'minutes' && type === 'hrs/min') {
        this.hoursInput()?.nativeElement.focus();
        event.preventDefault();
      }
    }

    if (event.key === 'ArrowRight') {
      if (component === 'hours' && type === 'hrs/min') {
        this.minutesInput()?.nativeElement.focus();
        event.preventDefault();
      } else if (component === 'minutes' && type === 'min/sec') {
        this.secondsInput()?.nativeElement.focus();
        event.preventDefault();
      }
    } else if (event.key === 'ArrowLeft') {
      if (component === 'seconds' && type === 'min/sec') {
        this.minutesInput()?.nativeElement.focus();
        event.preventDefault();
      } else if (component === 'minutes' && type === 'hrs/min') {
        this.hoursInput()?.nativeElement.focus();
        event.preventDefault();
      }
    }
  }

  /** Convert h/m/s display values to total seconds and update form control */
  private updateFormValue() {
    const h = parseInt(this.hours(), 10) || 0;
    const m = parseInt(this.minutes(), 10) || 0;
    const s = parseInt(this.seconds(), 10) || 0;
    const type = this.durationType() as DurationType;

    const primary =
      type === 'hrs/min' || type === 'hrs' ? h : type === 'min/sec' || type === 'min' ? m : type === 'sec' ? s : 0;
    const secondary = type === 'hrs/min' ? m : type === 'min/sec' ? s : 0;

    const totalSeconds = durationDisplayToSeconds(primary, secondary, type);
    this.control().setValue(totalSeconds === 0 ? null : String(totalSeconds));
  }

  /** Auto-advance to next field when current field is full */
  private handleAutoFocus(component: 'hours' | 'minutes' | 'seconds', value: string) {
    const type = this.durationType();
    setTimeout(() => {
      if (component === 'hours' && value.length === 3 && type === 'hrs/min') {
        this.minutesInput()?.nativeElement.focus();
      } else if (component === 'minutes' && value.length === 2 && type === 'min/sec') {
        this.secondsInput()?.nativeElement.focus();
      }
    }, 0);
  }
}
